import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import { ButtonShell, ControlledPopover, Image } from '@round/ui-kit';
import { ActionCard } from '@round/whitelabel-ui';
import cn from 'classnames';
import useOnClickOutside from 'Hooks/useOnClickOutside';
import useProjectDetails from '../../../../useProjectDetails';
import LinkProjectPopout from '../LinkProjectPopout/LinkProjectPopout';
import popoutStyles from '../../popout.module.css';
import styles from './ReceivedProjectRunBy.module.css';

const ReceivedProjectRunBy = () => {
    const { project, isLoading, partnership, isPartnershipLoading } = useProjectDetails();
    const isLinking = useIsMutating({ mutationKey: creatorbaseQueries.projectShares.keys.link() }) > 0;

    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [popout, setPopout] = useState<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    useOnClickOutside([anchor, popout], () => {
        if (!isLinking) {
            setIsOpen(false);
        }
    });

    const isRunByLoading = isLoading || isPartnershipLoading;
    const card = (
        <ActionCard
            className={cn(styles.card, styles.mutedSubTitle)}
            title={partnership?.agency_name ?? '-'}
            subTitle="+ Link to project"
            isLoading={isRunByLoading}
            Icon={({ className }) => (
                <Image
                    className={cn(className, styles.logo)}
                    src={partnership?.agency_logo ?? ''}
                    alt={partnership?.agency_name}
                />
            )}
        />
    );

    if (!project || isRunByLoading) {
        return card;
    }

    const handleClose = () => {
        setIsOpen(false);
        anchor?.focus();
    };

    return (
        <>
            <ButtonShell
                ref={setAnchor}
                className={popoutStyles.trigger}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                disabled={isLinking}
                onClick={() => setIsOpen((isOpen) => !isOpen)}
            >
                {card}
            </ButtonShell>

            <ControlledPopover
                isOpen={isOpen}
                anchorElement={anchor}
                options={{
                    placement: 'bottom-end',
                    modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                }}
                overlayClassName={popoutStyles.overlay}
            >
                <div
                    ref={setPopout}
                    className={popoutStyles.popout}
                    role="dialog"
                    aria-label="Link to project"
                    tabIndex={-1}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && !isLinking) {
                            handleClose();
                        }
                    }}
                >
                    <LinkProjectPopout project={project} onClose={handleClose} />
                </div>
            </ControlledPopover>
        </>
    );
};

export default ReceivedProjectRunBy;
